<template>
  <div class="space-y-[3.75rem]">
    <section v-for="event in events" :key="event.id" :aria-labelledby="`selfscan-event-${event.id}`">
      <div class="mb-5 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 :id="`selfscan-event-${event.id}`" class="h3">{{ event.name }}</h2>
          <p class="mt-1 flex items-center gap-2 text-sm text-text">
            <CalendarIcon class="h-4 w-4 fill-current" />
            {{ formatTimestamp(event.eventtime) }}
          </p>
        </div>
        <p v-if="!canActivate(event)" class="text-sm text-text">
          {{ $t('selfscan.opens_at', { date: formatTimestamp(event.eventtime - activationWindow) }) }}
        </p>
      </div>

      <div class="overflow-x-auto rounded-md border border-surface-grey">
        <table class="w-full table-fixed border-collapse text-left md:min-w-[520px] md:table-auto">
          <thead class="bg-surface-grey-light text-xs uppercase text-text">
            <tr>
              <th class="w-[33%] px-2 py-4 font-semibold md:w-auto md:px-5">{{ $t('selfscan.ticket_type') }}</th>
              <th class="w-[33%] px-2 py-4 font-semibold md:w-auto md:px-5">{{ $t('selfscan.ticket_number') }}</th>
              <th class="w-[34%] px-2 py-4 text-center font-semibold md:w-auto md:px-5">{{ $t('selfscan.status') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="ticket in event.tickets"
              :key="ticket.ticket_number"
              class="border-t border-surface-grey"
            >
              <td class="min-w-0 break-words px-2 py-5 text-headings md:px-5">{{ ticket.name1 }}</td>
              <td class="w-[33%] px-2 py-5 text-text md:w-auto md:px-5">{{ ticket.ticket_number }}</td>
              <td class="w-[34%] px-2 py-5 text-right md:w-auto md:px-5">
                <TicketAction
                  :data-selfscan-ticket="ticket.ticket_number"
                  :ticket="ticket"
                  :disabled="!canActivate(event)"
                  @select="$emit('select', event, ticket)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

    </section>
  </div>
</template>

<script setup lang="ts">
  import { defineComponent, h } from 'vue';
  import { useI18n } from 'vue-i18n';

  import CalendarIcon from '@/components/icons/CalendarIcon.vue';
  import BaseButton from '@/components/ui/BaseButton.vue';
  import type { SelfscanTicket } from '@/types';
  import { formatDate } from '@/utils/formatDate';

  export interface SelfscanDisplayEvent {
    id: number;
    name: string;
    eventtime: number;
    tickets: SelfscanTicket[];
  }

  defineProps<{
    events: SelfscanDisplayEvent[];
  }>();

  defineEmits<{
    select: [event: SelfscanDisplayEvent, ticket: SelfscanTicket];
  }>();

  const { locale, t } = useI18n();
  const activationWindow = 24 * 60 * 60;

  const TicketAction = defineComponent({
    props: {
      ticket: { type: Object as () => SelfscanTicket, required: true },
      disabled: { type: Boolean, default: false },
    },
    emits: ['select'],
    setup(props, { emit, attrs }) {
      return () => {
        if (props.ticket.arrived) {
          return h(
            'button',
            {
              ...attrs,
              type: 'button',
              class: [
                attrs.class,
                'inline-flex cursor-pointer items-center justify-center rounded-full border border-surface-grey bg-surface-light px-4 py-2 text-sm font-medium text-headings hover:bg-surface-grey-light focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
              ],
              onClick: () => emit('select'),
            },
            t('selfscan.activated')
          );
        }

        return h(
          BaseButton,
          {
            ...attrs,
            color: 'primary',
            size: 'sm',
            disabled: props.disabled,
            onClick: () => emit('select'),
          },
          () => t('selfscan.activate')
        );
      };
    },
  });

  function canActivate(event: SelfscanDisplayEvent) {
    return Date.now() / 1000 + activationWindow > event.eventtime;
  }

  function formatTimestamp(timestamp: number) {
    return formatDate(timestamp, { locale: locale.value, showTime: true });
  }

  function focusTicket(ticketNumber: string) {
    const actions = Array.from(document.querySelectorAll<HTMLElement>('[data-selfscan-ticket]'));
    const matchingActions = actions.filter(element => element.dataset.selfscanTicket === ticketNumber);
    (matchingActions.find(element => element.offsetParent !== null) ?? matchingActions[0])?.focus();
  }

  defineExpose({ focusTicket });
</script>